import React, { useEffect, useState, useMemo } from "react";
import {
  Pencil,
  GraduationCap,
  Trophy,
  FileText,
  Layers3,
  ClipboardCheck,
  Clock3,
  CheckCircle2,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "../../axios";

function DraftCompetencyPage({ filter }) {
  const [draftCompetencyList, setDrafCompetencyList] = useState([]);
  const [isGettingData, setIsGettingData] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  const teacherId = JSON.parse(localStorage.getItem("Teacher_id"));

  let timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );
  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );

  const navigate = useNavigate();
  const schoolId = JSON.parse(localStorage.getItem("schoolId"));

  const getDraftAssessment = async () => {
    setIsGettingData(true);
    try {
      const res = await axios.get(
        `/api/v1/competency/evaluations?schoolId=${schoolId}&status=draft&academicYearSessionId=${academicSessionId}&timetableSessionId=${timeTableSessionId}&teacherId=${teacherId}` +
          `${filter?.standard_id ? `&standardId=${filter.standard_id}` : ""}` +
          `${filter?.section_id ? `&sectionId=${filter.section_id}` : ""}`,
      );
      setDrafCompetencyList(res?.data?.data || []);
    } catch (error) {
      console.log(error);
      setDrafCompetencyList([]);
    } finally {
      setIsGettingData(false);
    }
  };

  useEffect(() => {
    getDraftAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.standard_id, filter?.section_id]);

  // ─── 🔥 FRONTEND SEARCH FILTER ──────────────────
  const filteredList = useMemo(() => {
    if (!filter?.searchvalue) {
      return draftCompetencyList;
    }

    const searchTerm = filter.searchvalue.toLowerCase().trim();
    return draftCompetencyList.filter((assessment) => {
      const nameMatch =
        assessment?.name?.toLowerCase().includes(searchTerm) || false;

      const competencyMatch =
        assessment?.competencyId?.name?.toLowerCase().includes(searchTerm) ||
        false;

      const domainMatch =
        assessment?.domainId?.name?.toLowerCase().includes(searchTerm) || false;

      const classMatch =
        assessment?.standardId?.name?.toLowerCase().includes(searchTerm) ||
        false;

      const sectionMatch =
        assessment?.sectionId?.name?.toLowerCase().includes(searchTerm) ||
        false;

      const evalTypeMatch =
        assessment?.evaluationTypeId?.name
          ?.toLowerCase()
          .includes(searchTerm) || false;

      // 👈 Assessment Event (backward compatible with reportCycleId)
      const assessmentEventMatch =
        assessment?.assessmentEventId?.name
          ?.toLowerCase()
          .includes(searchTerm) ||
        assessment?.reportCycleId?.name?.toLowerCase().includes(searchTerm) ||
        false;

      return (
        nameMatch ||
        competencyMatch ||
        domainMatch ||
        classMatch ||
        sectionMatch ||
        evalTypeMatch ||
        assessmentEventMatch
      );
    });
  }, [draftCompetencyList, filter?.searchvalue]);

  const handleEdit = (assessment) => {
    navigate("/draft-competency-report", {
      state: {
        assessmentId: assessment?._id,
        assessment,
      },
    });
  };

  const openDeleteModal = (e, assessment) => {
    e.stopPropagation();
    setAssessmentToDelete(assessment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!assessmentToDelete) return;

    const assessmentId = assessmentToDelete._id;
    setDeletingId(assessmentId);
    setShowDeleteModal(false);

    try {
      const response = await axios.delete(
        `/api/v1/competency/evaluations/${assessmentId}`,
      );

      if (response?.data?.success || response?.status === 200) {
        setDrafCompetencyList((prevList) =>
          prevList.filter((item) => item._id !== assessmentId),
        );
        console.log("Draft competency deleted successfully");
      } else {
        console.error(
          "Delete failed:",
          response?.data?.message || "Unknown error",
        );
        alert("Failed to delete. Please try again.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(
        error?.response?.data?.message ||
          "Something went wrong while deleting.",
      );
    } finally {
      setDeletingId(null);
      setAssessmentToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusValue = status?.toLowerCase();
    if (statusValue === "draft") {
      return {
        text: "Draft",
        icon: <Clock3 size={12} />,
        bg: "#fff7ed",
        color: "#c2410c",
        border: "#fed7aa",
      };
    }
    return {
      text: status || "N/A",
      icon: <CheckCircle2 size={12} />,
      bg: "#f0fdf4",
      color: "#166534",
      border: "#bbf7d0",
    };
  };

  const getRubricScale = (assessment) => {
    const scale = assessment?.rubricScaleId;
    if (!scale) return "N/A";
    const itemCount = Array.isArray(scale?.rubricItems)
      ? scale.rubricItems.length
      : 0;
    return itemCount > 0
      ? `${scale.name} (${itemCount} Levels)`
      : scale.name || "N/A";
  };

  if (isGettingData) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div
          className="spinner-border"
          style={{ color: "#f77f00" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (filteredList.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center py-5 w-100"
        style={{ minHeight: "300px", color: "#9ca3af" }}
      >
        <GraduationCap
          size={48}
          className="mb-3"
          style={{ opacity: 0.4, color: "#d1d5db" }}
        />
        <p className="fw-semibold fs-5" style={{ color: "#4b5563" }}>
          🧾{" "}
          {filter?.searchvalue
            ? "No Matching Draft Found"
            : "No Draft Competency Found"}
        </p>
        <p className="small">
          {filter?.searchvalue
            ? `No results found for "${filter.searchvalue}"`
            : "Try adjusting filters or add a new Competency."}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowDeleteModal(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#6b7280",
              }}
            >
              <X size={20} />
            </button>

            <div className="text-center mb-4">
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "#fef2f2",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px auto",
                  color: "#dc2626",
                }}
              >
                <Trash2 size={28} />
              </div>
              <h5 className="fw-bold" style={{ color: "#1f2937" }}>
                Delete Competency?
              </h5>
              <p
                className="mb-0"
                style={{ color: "#6b7280", fontSize: "14px" }}
              >
                Are you sure you want to delete <br />
                <strong>{assessmentToDelete?.name || "this draft"}</strong>?
                <br />
                This action cannot be undone.
              </p>
            </div>

            <div className="d-flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn w-100"
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#fff",
                  color: "#4b5563",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn w-100"
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  border: "none",
                  backgroundColor: "#dc2626",
                  color: "#fff",
                  fontWeight: 600,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#b91c1c")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#dc2626")
                }
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="w-100"
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <div
          className="table-responsive"
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflow: "auto",
          }}
        >
          <table
            className="table align-middle mb-0"
            style={{
              fontSize: "13px",
              minWidth: "1200px",
            }}
          >
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#f9fafb",
                }}
              >
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Class
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Assessment
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Domain
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Competency
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Evaluation Type
                </th>
                {/* 👈 renamed from Report Cycle */}
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Assessment Event
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Rubric Scale
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Status
                </th>
                <th
                  className="text-center fw-bold py-3 px-3"
                  style={headerStyle}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredList.map((assessment, idx) => {
                const statusBadge = getStatusBadge(assessment?.status);
                const isLast = idx === filteredList.length - 1;
                const isDeleting = deletingId === assessment?._id;

                return (
                  <tr
                    key={assessment?._id}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      borderBottom: isLast ? "none" : "1px solid #f3f4f6",
                    }}
                    onClick={() => handleEdit(assessment)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fffbeb";
                      e.currentTarget.style.transform = "scale(1.001)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    {/* CLASS */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#f3f4f6",
                          color: "#374151",
                          padding: "5px 11px",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 700,
                          border: "1px solid #e5e7eb",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {assessment?.standardId?.name || "N/A"} —{" "}
                        {assessment?.sectionId?.name || "N/A"}
                      </span>
                    </td>
                    {/* ASSESSMENT NAME */}
                    <td className="py-3 px-3">
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ minWidth: "220px" }}
                      >
                        <div
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "9px",
                            backgroundColor: "#fdf4ff",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <ClipboardCheck size={17} />
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              color: "#1f2937",
                              lineHeight: "18px",
                            }}
                          >
                            {assessment?.name || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* DOMAIN */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#f0fdf4",
                          color: "#15803d",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #bbf7d0",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Layers3 size={12} />
                        {assessment?.domainId?.name || "N/A"}
                      </span>
                    </td>
                    {/* COMPETENCY */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #bfdbfe",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <GraduationCap size={12} />
                        {assessment?.competencyId?.name || "N/A"}
                      </span>
                    </td>
                    {/* EVALUATION TYPE */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#fdf4ff",
                          color: "#7c3aed",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #e9d5ff",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <FileText size={12} />
                        {assessment?.evaluationTypeId?.name || "N/A"}
                      </span>
                    </td>
                    {/* ASSESSMENT EVENT (was Report Cycle) */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#fff7ed",
                          color: "#c2410c",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #fed7aa",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Trophy size={12} />
                        {/* 👈 Assessment Event name — backward compatible */}
                        {assessment?.assessmentEventId?.name ||
                          assessment?.reportCycleId?.name ||
                          "N/A"}
                      </span>
                    </td>
                    {/* RUBRIC SCALE */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: "#f0f9ff",
                          color: "#0369a1",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #bae6fd",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getRubricScale(assessment)}
                      </span>
                    </td>
                    {/* STATUS */}
                    <td className="text-center py-3 px-3">
                      <span
                        style={{
                          backgroundColor: statusBadge.bg,
                          color: statusBadge.color,
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "6px 12px",
                          borderRadius: "20px",
                          border: `1px solid ${statusBadge.border}`,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {statusBadge.icon}
                        {statusBadge.text}
                      </span>
                    </td>
                    {/* ACTIONS */}
                    <td className="text-center py-3 px-3">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button
                          className="btn d-inline-flex align-items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(assessment);
                          }}
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#fff",
                            background:
                              "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "10px",
                            boxShadow: "0 4px 12px rgba(59,130,246,0.25)",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 6px 20px rgba(59,130,246,0.35)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(59,130,246,0.25)";
                          }}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>

                        <button
                          className="btn d-inline-flex align-items-center gap-1"
                          onClick={(e) => openDeleteModal(e, assessment)}
                          disabled={isDeleting}
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#fff",
                            background: isDeleting
                              ? "#9ca3af"
                              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "10px",
                            boxShadow: isDeleting
                              ? "none"
                              : "0 4px 12px rgba(239,68,68,0.25)",
                            transition: "all 0.2s ease",
                            opacity: isDeleting ? 0.6 : 1,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                          }}
                          onMouseEnter={(e) => {
                            if (!isDeleting) {
                              e.currentTarget.style.transform =
                                "translateY(-2px)";
                              e.currentTarget.style.boxShadow =
                                "0 6px 20px rgba(239,68,68,0.35)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isDeleting) {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow =
                                "0 4px 12px rgba(239,68,68,0.25)";
                            }
                          }}
                        >
                          {isDeleting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                                style={{ width: "14px", height: "14px" }}
                              />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 size={14} />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const headerStyle = {
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "#6b7280",
  backgroundColor: "#f9fafb",
  borderBottom: "2px solid #e5e7eb",
};

export default DraftCompetencyPage;
